import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { 
  TrendingUp, TrendingDown, Briefcase, Target, 
  RefreshCw, Clock, Zap
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Stat Card Component - Compact design for the widget
const StatCard = ({ 
  label, 
  value, 
  subLabel, 
  icon: Icon, 
  variant = "default",
  isLoading = false 
}) => {
  const variants = {
    default: "from-zinc-500/10 to-zinc-500/5 border-zinc-500/20",
    profit: "from-emerald-500/15 to-emerald-500/5 border-emerald-500/30",
    loss: "from-rose-500/15 to-rose-500/5 border-rose-500/30",
    neutral: "from-cyan-500/10 to-cyan-500/5 border-cyan-500/20",
    warning: "from-amber-500/10 to-amber-500/5 border-amber-500/20"
  };
  
  const textColors = {
    default: "text-white",
    profit: "text-emerald-400",
    loss: "text-rose-400",
    neutral: "text-cyan-400",
    warning: "text-amber-400"
  };

  const iconColors = {
    default: "text-zinc-400",
    profit: "text-emerald-400",
    loss: "text-rose-400",
    neutral: "text-cyan-400",
    warning: "text-amber-400"
  };

  return (
    <div className={`bg-gradient-to-b ${variants[variant]} border rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]`}>
      <div className="flex items-start justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          {label}
        </span>
        {Icon && <Icon className={`w-4 h-4 ${iconColors[variant]}`} />}
      </div>
      
      {isLoading ? (
        <div className="h-8 flex items-center">
          <div className="w-20 h-6 bg-zinc-700/50 rounded animate-pulse" />
        </div>
      ) : (
        <div className={`font-mono text-2xl font-bold tracking-tight ${textColors[variant]}`}>
          {value}
        </div>
      )}
      
      {subLabel && (
        <div className="text-xs text-zinc-500 mt-1 font-mono">{subLabel}</div>
      )}
    </div>
  );
};

export const QuickStatsWidget = ({ accountId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchQuickStats = useCallback(async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setRefreshing(true);
    
    try {
      const params = new URLSearchParams();
      if (accountId) params.append('account_id', accountId);
      
      const response = await api.get(`/analytics/quick-stats?${params.toString()}`);
      if (response.data.status) {
        setStats(response.data.data);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error("Error fetching quick stats:", error);
      if (showRefreshIndicator) {
        toast.error(error.userMessage || "Failed to refresh quick stats");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [accountId]);

  useEffect(() => {
    fetchQuickStats();
    
    // Auto-refresh every 2 minutes
    const interval = setInterval(() => fetchQuickStats(false), 120000);
    return () => clearInterval(interval);
  }, [fetchQuickStats, accountId]);

  const handleRefresh = () => {
    fetchQuickStats(true);
  };

  // Format currency
  const formatCurrency = (value) => {
    if (typeof value !== 'number') return '₹0';
    const absValue = Math.abs(value);
    const sign = value >= 0 ? '+' : '-';
    return `${sign}₹${absValue.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Determine variant based on value
  const getPnlVariant = (value) => {
    if (value > 0) return "profit";
    if (value < 0) return "loss";
    return "neutral";
  };

  const todayRealizedPnl = stats?.today_realized_pnl || 0;
  const todayUnrealizedPnl = stats?.today_unrealized_pnl || 0;
  const openPositions = stats?.open_positions || 0;
  const weeklyWinRate = stats?.weekly_win_rate || 0;

  return (
    <div 
      data-testid="quick-stats-widget"
      className="rounded-xl border border-white/5 bg-zinc-900/40 backdrop-blur-md shadow-2xl shadow-black/40 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/5">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-cyan-400" />
          <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-400">
            Quick Stats
          </h3>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-[10px] text-zinc-600 font-mono flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            data-testid="quick-stats-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-zinc-400 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="Today's Realized"
          value={formatCurrency(todayRealizedPnl)}
          subLabel={`${stats?.today_realized_trades || 0} trades closed`}
          icon={todayRealizedPnl >= 0 ? TrendingUp : TrendingDown}
          variant={getPnlVariant(todayRealizedPnl)}
          isLoading={loading}
        />
        
        <StatCard
          label="Today's Unrealized"
          value={formatCurrency(todayUnrealizedPnl)}
          subLabel="MTM on open positions"
          icon={todayUnrealizedPnl >= 0 ? TrendingUp : TrendingDown}
          variant={getPnlVariant(todayUnrealizedPnl)}
          isLoading={loading}
        />
        
        <StatCard
          label="Open Positions"
          value={loading ? "—" : openPositions}
          subLabel="Active trades"
          icon={Briefcase}
          variant={openPositions > 0 ? "warning" : "neutral"}
          isLoading={loading}
        />
        
        <StatCard
          label="Weekly Win Rate"
          value={loading ? "—" : `${weeklyWinRate}%`}
          subLabel={`${stats?.weekly_wins || 0}W / ${stats?.weekly_losses || 0}L`}
          icon={Target}
          variant={weeklyWinRate >= 50 ? "profit" : weeklyWinRate > 0 ? "warning" : "neutral"}
          isLoading={loading}
        />
      </div>
    </div>
  );
};

export default QuickStatsWidget;
